// Презентация времени. Бэкенд отдаёт время в UTC (iso8601); всё показываемое
// время приводится к поясу смотрящего — `currentUser.timezone` (E213, ТЗ §7.5).
// В компонентах источник пояса — `auth.viewerTimezone` (auth-стор); сюда он
// передаётся явным параметром `timezone` каждой *InZone-функции.

const LOCALE = 'ru-RU'

/**
 * Пояс браузера — fallback, когда персональный `currentUser.timezone` недоступен
 * (до инициализации сессии / без логина). Источник истины для презентации —
 * `auth.viewerTimezone`, который и подставляет этот fallback.
 */
export function localTimezone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** «2026-06-05T09:30:00Z» + «Asia/Omsk» → «12:30». */
export function timeInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

/**
 * ISO → «HH:MM» (24ч, colon) для value `<input type="time">` — обратное к
 * `zonedInputToIso`. en-GB гарантирует формат ввода без локальных сепараторов.
 * Пара к `dateInZone` при префилле планового времени в форме редактирования заявки.
 */
export function timeInputInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** «2026-06-05T09:30:00Z» + «Asia/Omsk» → «5 июн, 12:30». */
export function dateTimeInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

/**
 * Метка пояса для кросс-филиальных списков: IANA → город. «Asia/Omsk» → «Омск».
 * Берём GMT-смещение через Intl, а человекочитаемое имя — из последнего сегмента
 * IANA (подчёркивания → пробелы). Локализованный перевод города — Phase 1.
 */
export function zoneLabel(timezone: string): string {
  const segment = timezone.split('/').at(-1) ?? timezone
  return segment.replace(/_/g, ' ')
}

/** timeZoneName-part Intl для пояса: город (shortGeneric), смещение (shortOffset) или аббревиатура (short, «МСК»). */
function zonePart(timezone: string, timeZoneName: 'shortGeneric' | 'shortOffset' | 'short'): string | null {
  const part = new Intl.DateTimeFormat(LOCALE, { timeZone: timezone, timeZoneName })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName')
  return part?.value ?? null
}

/**
 * Подпись пояса филиала «Город · UTC±N» (§7.5): «Europe/Moscow» → «Москва · UTC+3».
 * Город и смещение — из CLDR через Intl (generic-имя зоны = exemplar city для
 * региональных зон РФ), без своей таблицы городов. Невалидная зона — fallback
 * на zoneLabel (сегмент IANA), без падения рендера.
 */
export function zoneTitle(timezone: string): string {
  try {
    const city = zonePart(timezone, 'shortGeneric')
    const offset = zonePart(timezone, 'shortOffset')?.replace('GMT', 'UTC')
    if (city === null || offset === undefined) return zoneLabel(timezone)
    return `${city} · ${offset}`
  } catch {
    return zoneLabel(timezone)
  }
}

/** Смещение пояса (мс, восток — положительное) на конкретный момент. */
function zoneOffsetMs(timezone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)
  const map: Record<string, number> = {}
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = Number(part.value)
  }
  const asUtc = Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute, map.second)
  return asUtc - instant.getTime()
}

/**
 * Обратная к *InZone: ввод «дата + время по стенным часам пояса смотрящего» →
 * UTC iso8601 для бэка. Планирование заявки (E27 §4.13) — юзер вводит `planned_at`
 * в своём поясе (`auth.viewerTimezone`); РФ-зоны без перехода на летнее время,
 * поэтому смещение на момент даты однозначно.
 */
// Плановая заявка только с датой: пустое время → активируется в 09:00 (#667, #674).
export const DEFAULT_PLANNED_TIME = '09:00'

export function zonedInputToIso(date: string, time: string, timezone: string): string {
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute)
  const offset = zoneOffsetMs(timezone, new Date(wallAsUtc))
  return new Date(wallAsUtc - offset).toISOString()
}

/**
 * Прошедшее время «M:SS» / «H:MM:SS» — тикающий счётчик «свободна 0:43»
 * в очереди заявок (§4.13). Отрицательная дельта (рассинхрон часов) → «0:00».
 */
export function formatElapsed(fromIso: string, now: Date): string {
  const totalSeconds = Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 1000))
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60) % 60
  const hours = Math.floor(totalSeconds / 3600)
  const ss = String(seconds).padStart(2, '0')
  if (hours === 0) return `${minutes}:${ss}`
  return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
}

/**
 * Длительность ожидания заявки в человеческих единицах (макет refined-requests:
 * «18 мин», «3 ч», «5 дн») — вместо секундного `formatElapsed`, который на
 * заявке недельной давности выдаёт бессмысленное «786:13:28». Отдаёт { unit, value }
 * для i18n-плюрализации; текущий момент — параметр (тестируемость, без Date.now()).
 */
export function waitDuration(
  fromIso: string,
  now: Date,
): { unit: 'minutes' | 'hours' | 'days'; value: number } {
  const totalMinutes = Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 60_000))
  if (totalMinutes < 60) return { unit: 'minutes', value: totalMinutes }
  const hours = Math.floor(totalMinutes / 60)
  if (hours < 24) return { unit: 'hours', value: hours }
  return { unit: 'days', value: Math.floor(hours / 24) }
}

/** Дата (YYYY-MM-DD) в поясе филиала — для сравнения «сегодня/просрочено». */
export function dateInZone(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
  return parts
}

/** «2026-05-23T...» + «Europe/Moscow» → «23.05». Компактная дата для колонки ленты отчёта. */
export function dateShortInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** «2026-05-23T...» + «Europe/Moscow» → «Сб». Короткий день недели (колонка «День»). */
export function weekdayShortInZone(iso: string, timezone: string): string {
  const short = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short',
    timeZone: timezone,
  }).format(new Date(iso))
  return short.charAt(0).toUpperCase() + short.slice(1)
}

/** «2026-06-05T09:30:00Z» + «Europe/Moscow» → «5 июня 2026». Полная дата с годом (карточка контрагента). */
export function dateLongInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(iso))
}

/** «2026-06-05T09:30:00Z» + «Asia/Omsk» → «5 июня». Без года — для группировки таймлайна по дням. */
export function dayInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
  }).format(new Date(iso))
}

/**
 * Заголовок группы дня таймлайна. Относительность считается по поясу филиала
 * (день записи vs день now). kind = today/yesterday/date — строку рисует вызывающий
 * через i18n (label — дата для kind: 'date'/'today'/'yesterday' как подпись).
 */
export function dayHeading(
  iso: string,
  timezone: string,
  now: Date,
): { kind: 'today' | 'yesterday' | 'date'; label: string } {
  const day = dateInZone(iso, timezone)
  const today = dateInZone(now.toISOString(), timezone)
  const yesterday = dateInZone(new Date(now.getTime() - 86_400_000).toISOString(), timezone)
  const label = dayInZone(iso, timezone)
  if (day === today) return { kind: 'today', label }
  if (day === yesterday) return { kind: 'yesterday', label }
  return { kind: 'date', label }
}

/**
 * Единое относительное время метки «когда произошло/создано» (списки, ленты,
 * детали, уведомления, аудит). Возвращает структуру для i18n-плюрализации (как
 * `waitDuration`/`relationAge`) — строку рисует `RelativeTime.vue` через `t()`.
 * Текущий момент — параметр (тестируемость, без скрытого `Date.now()`).
 *
 * Шкала: < 1 мин → «только что»; < 60 мин → N минут; < 24 ч (по дельте) → N часов;
 * далее по КАЛЕНДАРНЫМ дням пояса → вчера / позавчера; ещё раньше → дата
 * («25 июня»; год добавляется, только если он не текущий — «25 июня 2024»).
 */
export type RelativeTimeParts =
  | { kind: 'now' }
  | { kind: 'minutes'; value: number }
  | { kind: 'hours'; value: number }
  | { kind: 'yesterday' }
  | { kind: 'before_yesterday' }
  | { kind: 'date'; label: string }

export function relativeTime(iso: string, timezone: string, now: Date): RelativeTimeParts {
  const diffMinutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000)
  if (diffMinutes < 1) return { kind: 'now' }
  if (diffMinutes < 60) return { kind: 'minutes', value: diffMinutes }
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return { kind: 'hours', value: diffHours }

  // ≥ 24 ч: относительность по календарным дням пояса (§7.5), а не по дельте —
  // «вчера/позавчера» считаем по стенному дню, чтобы не зависеть от часа суток.
  const day = dateInZone(iso, timezone)
  const yesterday = dateInZone(new Date(now.getTime() - 86_400_000).toISOString(), timezone)
  const beforeYesterday = dateInZone(new Date(now.getTime() - 2 * 86_400_000).toISOString(), timezone)
  if (day === yesterday) return { kind: 'yesterday' }
  if (day === beforeYesterday) return { kind: 'before_yesterday' }

  const thisYear = dateInZone(now.toISOString(), timezone).slice(0, 4)
  const label = day.slice(0, 4) === thisYear ? dayInZone(iso, timezone) : dateLongInZone(iso, timezone)
  return { kind: 'date', label }
}

/** Полная дата+время+пояс для тултипа над относительным временем: «24 июня 2025 г., 16:44 МСК». */
export function fullDateTimeInZone(iso: string, timezone: string): string {
  const zone = zonePart(timezone, 'short')
  return zone === null ? dateTimeLongInZone(iso, timezone) : `${dateTimeLongInZone(iso, timezone)} ${zone}`
}

/** «2026-07-05T...» + «Europe/Moscow» → «5 июля 2026 г., 15:45». Полная дата+время без пояса — метка «Создано» на карточке детали (места достаточно, не сокращаем). */
export function dateTimeLongInZone(iso: string, timezone: string): string {
  return `${dateLongInZone(iso, timezone)}, ${timeInZone(iso, timezone)}`
}

/**
 * Давность отношений (§4.5) — сколько времени с первого контакта. Возвращает
 * { unit, value } для плюрализации через i18n: < 30 дней — дни, иначе — месяцы.
 * Текущий момент — параметр (тестируемость, без скрытого Date.now()).
 */
export function relationAge(fromIso: string, now: Date): { unit: 'days' | 'months'; value: number } {
  const days = Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 86_400_000))
  if (days < 30) return { unit: 'days', value: days }
  return { unit: 'months', value: Math.floor(days / 30) }
}

/**
 * Периоды отчёта: 4 пресета-диапазона + «произвольный» (custom — свои границы
 * date_from/date_to; у него нет предустановленных bounds, режим держится флагом).
 */
export type ReportPeriod = 'current_month' | 'previous_month' | 'quarter' | 'year' | 'custom'

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Диапазон «первый день startMonth — последний день endMonth» года year, YYYY-MM-DD. */
function monthRange(year: number, startMonth: number, endMonth: number): { from: string; to: string } {
  const lastDay = new Date(Date.UTC(year, endMonth, 0)).getUTCDate()
  return { from: `${year}-${pad2(startMonth)}-01`, to: `${year}-${pad2(endMonth)}-${pad2(lastDay)}` }
}

/**
 * Границы периода-пресета [from, to] (YYYY-MM-DD) в поясе смотрящего (§7.5):
 * «сегодня» берётся в поясе tz, месяц/квартал/год — календарные. Границы шлём
 * бэку как date_from/date_to (ADDENDUM §F, произвольного диапазона в макете нет).
 */
export function reportPeriodBounds(
  period: ReportPeriod,
  now: Date,
  timezone: string,
): { from: string; to: string } {
  const [year, month] = dateInZone(now.toISOString(), timezone).split('-').map(Number)
  if (period === 'previous_month') {
    const y = month === 1 ? year - 1 : year
    const m = month === 1 ? 12 : month - 1
    return monthRange(y, m, m)
  }
  if (period === 'quarter') {
    const start = Math.floor((month - 1) / 3) * 3 + 1
    return monthRange(year, start, start + 2)
  }
  if (period === 'year') return monthRange(year, 1, 12)
  return monthRange(year, month, month)
}

/**
 * Подпись «Июль 2026» месяца со смещением monthOffset от текущего (в поясе tz) —
 * динамические подписи пресетов периода (ADDENDUM §F). formatToParts отсекает
 * суффикс «г.» ru-локали; первая буква заглавная (стиль макета).
 */
export function monthYearLabel(now: Date, timezone: string, monthOffset: number): string {
  const [year, month] = dateInZone(now.toISOString(), timezone).split('-').map(Number)
  const target = new Date(Date.UTC(year, month - 1 + monthOffset, 1, 12))
  const parts = new Intl.DateTimeFormat(LOCALE, {
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  }).formatToParts(target)
  const monthName = parts.find((p) => p.type === 'month')?.value ?? ''
  const yearName = parts.find((p) => p.type === 'year')?.value ?? ''
  const label = `${monthName} ${yearName}`
  return label.charAt(0).toUpperCase() + label.slice(1)
}
